import React from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Container, Box, Paper, Typography, TextField, Button, Stack, Avatar, Alert, Divider } from '@mui/material'

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } }

interface UserData {
  id: number
  email: string
  name: string
  bio: string | null
  avatar_url: string | null
}

interface Props {
  user: UserData
  errors?: Record<string, string[]>
}

interface SharedProps {
  flash?: { notice?: string | null; alert?: string | null }
}

export default function UserEdit({ user, errors: serverErrors = {} }: Props) {
  const page = usePage<SharedProps>()
  const flash = page.props.flash || {}

  const profileForm = useForm({
    user: {
      name: user.name || '',
      email: user.email,
      bio: user.bio || '',
      avatar: null as File | null,
    },
  })

  const passwordForm = useForm({
    user: {
      current_password: '',
      password: '',
      password_confirmation: '',
    },
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    profileForm.transform((data) => {
      const payload: Record<string, unknown> = { ...data.user }
      if (!payload.avatar) delete payload.avatar
      return { user: payload }
    })
    profileForm.put('/users')
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    passwordForm.put('/users')
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Edit profile</Typography>

      {flash.notice && <Alert severity="success" sx={{ mb: 2 }}>{flash.notice}</Alert>}
      {flash.alert && <Alert severity="error" sx={{ mb: 2 }}>{flash.alert}</Alert>}

      <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4 }}>
        <form onSubmit={handleProfileSubmit}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={user.avatar_url || undefined}
                sx={{ width: 80, height: 80, fontSize: '2rem', fontWeight: 700 }}
              >
                {user.name.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Button
                variant="contained"
                component="label"
                size="small"
                sx={{ alignSelf: 'center', flexShrink: 0 }}
              >
                {profileForm.data.user.avatar ? `Selected: ${profileForm.data.user.avatar.name}` : 'Choose avatar'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => profileForm.setData('user', {
                    ...profileForm.data.user,
                    avatar: e.target.files?.[0] || null,
                  })}
                />
              </Button>
            </Stack>

            <TextField
              label="Name"
              value={profileForm.data.user.name}
              onChange={(e) => profileForm.setData('user', { ...profileForm.data.user, name: e.target.value })}
              error={Boolean(serverErrors.name)}
              helperText={serverErrors.name?.join(', ')}
              required
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Email"
              type="email"
              value={profileForm.data.user.email}
              onChange={(e) => profileForm.setData('user', { ...profileForm.data.user, email: e.target.value })}
              error={Boolean(serverErrors.email)}
              helperText={serverErrors.email?.join(', ')}
              required
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Bio"
              value={profileForm.data.user.bio}
              onChange={(e) => profileForm.setData('user', { ...profileForm.data.user, bio: e.target.value })}
              error={Boolean(serverErrors.bio)}
              helperText={serverErrors.bio?.join(', ')}
              multiline
              minRows={3}
              fullWidth
              sx={inputSx}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={profileForm.processing} size="small">
                Save profile
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Paper sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Change password</Typography>
        <form onSubmit={handlePasswordSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type="password"
              value={passwordForm.data.user.current_password}
              onChange={(e) => passwordForm.setData('user', { ...passwordForm.data.user, current_password: e.target.value })}
              error={Boolean(serverErrors.current_password)}
              helperText={serverErrors.current_password?.join(', ')}
              required
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="New password"
              type="password"
              value={passwordForm.data.user.password}
              onChange={(e) => passwordForm.setData('user', { ...passwordForm.data.user, password: e.target.value })}
              error={Boolean(serverErrors.password)}
              helperText={serverErrors.password?.join(', ')}
              required
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={passwordForm.data.user.password_confirmation}
              onChange={(e) => passwordForm.setData('user', { ...passwordForm.data.user, password_confirmation: e.target.value })}
              required
              fullWidth
              sx={inputSx}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={passwordForm.processing} size="small">
                Change password
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Container>
  )
}
